import { toRaw, unref } from 'vue'
import L from 'leaflet'
import { ShipxyOptions } from '@map-sdk/sdk/config'
import { CanvasShipUtils } from '@map-sdk/sdk/utils/CanvasShipUtils'
import { CommUtils } from '@map-sdk/sdk/utils/CommUtils'
import { tileLayerFunctional } from '@map-sdk/sdk/plugins/weather/util/TileLayerFunctional'
import { showWeatherTipMessage } from './ShowWeatherTipMessage'
import type { MyMap } from '@map-sdk/sdk/ShipxyAPISDK'
import type { TileLayerFunctional } from '@map-sdk/sdk/plugins/weather/util/TileLayerFunctional'

export interface LTileLayerFunctionalOptions {
  minZoom?: number
  maxZoom?: number
  subdomains?: string
}

class ShowFullScreenTitleLayer {
  options: LTileLayerFunctionalOptions
  _map: MyMap
  _weatherTileLayer: Record<string, TileLayerFunctional> = {}
  weatherTitleLayerUrl: Record<string, Record<string, string>> = {
    '0': {
      url: ShipxyOptions.weather.waterTemperatureTitleUrl,
      msgCode: 'showWaterTemperature',
    },
    '1': {
      url: ShipxyOptions.weather.iceConditionTitleUrl,
      msgCode: 'showIceCondition',
    },
    '2': { url: ShipxyOptions.weather.seaIceTitleUrl, msgCode: 'showSeaIce' },
    '3': {
      url: ShipxyOptions.weather.seaBreezeTitleUrl,
      msgCode: 'showSeaBreezeArrow',
    },
    '4': {
      url: ShipxyOptions.weather.seaBreezeArrowTitleUrl,
      msgCode: 'showSeaBreezeArrow',
    },
    '5': {
      url: ShipxyOptions.weather.rainfallTitleUrl,
      msgCode: 'showRainfall',
    },
    '6': {
      url: ShipxyOptions.weather.atmosphericPressureTitleUrl,
      msgCode: 'showAtmosphericPressure',
    },
    '7': {
      url: ShipxyOptions.weather.visibilityTitleUrl,
      msgCode: 'showVisibility',
    },
    '8': {
      url: ShipxyOptions.weather.airTemperatureTitleUrl,
      msgCode: 'showAirTemperature',
    },
    '9': { url: ShipxyOptions.weather.seaWaveTitleUrl, msgCode: 'showHaiLang' },
    '10': {
      url: ShipxyOptions.weather.waveArrowTitleUrl,
      msgCode: 'showHaiLang',
    },
  }
  weatherEvent: Record<string, any> = {}
  constructor(map: MyMap, options?: LTileLayerFunctionalOptions) {
    this.options = L.Util.extend({}, options)
    this._map = toRaw(unref(map))
  }

  showFullScreenTileLayer(
    type: string,
    timeStamps: any,
    customTimes?: { [type: string]: { time: string } }
  ) {
    const layerNamesArray = type.split(',')
    layerNamesArray.forEach((item) => {
      this.hideFullScreenTileLayer(item)
      if (timeStamps && timeStamps.length) {
        const formattedTimes: string[] = []
        const typeName = `type_${item}`
        if (customTimes && customTimes[typeName]) {
          formattedTimes.push(customTimes[typeName].time)
        } else {
          // 否则使用默认时间
          formattedTimes.push(
            `${CanvasShipUtils.dataFormat(new Date(), 'YYYY-MM-DD')} 20:00:00`
          )
        }
        this._weatherTileLayer[`${item}`] = this._getFullScreenLayer(
          item,
          formattedTimes as any,
          timeStamps
        )
        if (this._weatherTileLayer[`${item}`]) {
          this._weatherTileLayer[`${item}`].addTo(this._map)
          this._weatherTileLayer[`${item}`].bringToFront()
        } else {
          console.log('气象栅格图层，无数据！')
        }
      } else {
        console.error('气象栅格图层，参数非法！')
      }
    })
    this.weatherEvent[`${type}`] = showWeatherTipMessage(
      this._map,
      this.weatherTitleLayerUrl[layerNamesArray[0]].msgCode
    )
  }

  hideFullScreenTileLayer(type?: string) {
    if (type) {
      const types = type.split(',')
      this.weatherEvent[type] && this.weatherEvent[type].offRegisterEvent()
      types.forEach((item) => {
        console.log(this._weatherTileLayer, this._weatherTileLayer[item], item)
        if (this._weatherTileLayer[item]) {
          this._weatherTileLayer[item].remove()
          delete this._weatherTileLayer[item]
        }
      })

      return
    }

    Object.keys(this._weatherTileLayer).forEach((key) => {
      this._weatherTileLayer[key].remove()
      delete this._weatherTileLayer[key]
    })
    Object.keys(this.weatherEvent).forEach((key) => {
      this.weatherEvent[key] && this.weatherEvent[key].offRegisterEvent()
    })
  }

  _getFullScreenLayer(
    type: string,
    time: string,
    data: any,
    minZoom?: number,
    maxZoom?: number
  ): TileLayerFunctional {
    const weatherTileUrl = this.weatherTitleLayerUrl[type].url
      .replace('{k}', (this as any)._map.options.ak)
      .replace('{type}', type)
      .replace('{time}', time)
      .replace('{year}', CommUtils.dataFormat(new Date(), 'YYYY'))
      .replace('{forecasttime}', data)
    minZoom = minZoom || 2
    maxZoom = maxZoom || 7
    return tileLayerFunctional(
      (val) => {
        return weatherTileUrl
          .replace('{z}', val.zoom.toString())
          .replace('{x}', val.tile.column.toString())
          .replace('{y}', val.tile.row.toString())
          .replace('{zl}', val.zoom.toString())
      },
      {
        minZoom,
        maxZoom,
        subdomains: 'weatherTile',
      }
    )
  }
}

const showFullScreenTitleLayer = (
  map: MyMap,
  options?: LTileLayerFunctionalOptions
) => new ShowFullScreenTitleLayer(map, options)

export { ShowFullScreenTitleLayer, showFullScreenTitleLayer }
